// @flow
import { useCallback, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as csx from 'csstips';
import classify from '@src/utils/typestyle';
import { buildQueryString } from '@src/utils/general';

import { CompatibilityContext } from '../../contexts/compatibility';

import CertificateInfo from '@components/productDetail/CertificateInfo';
import BackToSearchLink from '@components/productDetail/BackToSearchLink';
import ComboButton from '../buttons/ComboButton';

import { trackAddToCart } from '../../utils/googleAnalytics';

import type { DiamondType } from '@types/diamond';
import * as dyorRoutes from '../../app_routes/ringBuilder';
import * as settingRoutes from '../../app_routes/settings';

const fullWidthChooseDiamondButton = {
  ...csx.vertical,
  ...csx.start,
  '> div': {
    width: '100%'
  },
  '> div:first-child': {
    marginBottom: '20px'
  }
};

const classes = classify({
  container: {
    ...csx.horizontal,
    ...csx.betweenJustified,
    ...csx.start,
    margin: '15px 0',
    paddingTop: 15,
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    '@media (max-width: 439px)': fullWidthChooseDiamondButton,
    '@media (min-width: 680px) and (max-width: 829px)': fullWidthChooseDiamondButton
  },
  leftSection: {
    maxHeight: '88px'
  },
  rightSection: {
    flexBasis: 190,
    textAlign: 'center',

    '@media (max-width: 439px)': { flexBasis: 'auto' },
    '@media (min-width: 680px) and (max-width: 829px)': { flexBasis: 'auto' },

    $nest: {
      '& > *:nth-child(1)': {
        marginBottom: 4
      }
    }
  }
});

type PropsType = {
  diamond: DiamondType
};

export default function DiamondActions({ diamond }: PropsType) {
  const searchFilters = useSelector(state => state.diamondSearchFilters);
  const savedDiamonds = useSelector(state => state.diamondComparison.ids);
  const searchHref = `/diamonds/search${buildQueryString(
    searchFilters,
    'diamonds'
  )}`;
  const { compatibility } = useContext(CompatibilityContext);

  const currentDyorDiamondId = useSelector(
    state => state.ringBuilder.diamond.id
  );
  const currentSettingSku = useSelector(state => state.ringBuilder.setting.sku);
  const dispatch = useDispatch();
  const handleItemClick = useCallback(
    (name: string) => {
      if (!diamond) return;

      if (name === 'addToCompare') {
        dispatch({
          type: 'TOGGLE_DIAMOND_FOR_COMPARISON',
          item: diamond.list_service_diamond_id,
          redirectTo: '/saved'
        });
      }

      if (name === 'addToDyor') {
        dispatch({
          type: 'SELECT_DYOR_SETTING_SKU_AND_DIAMOND_ID',
          payload: {
            settingSku: compatibility.recommended || currentSettingSku,
            diamondId: diamond.list_service_diamond_id
          },
          redirectTo:
            (currentSettingSku &&
              dyorRoutes.combinedStep({
                diamond: diamond.list_service_diamond_id,
                setting: compatibility.recommended || currentSettingSku
              })) ||
            settingRoutes.searchPath()
        });
      }

      if (name === 'addToBag') {
        trackAddToCart(diamond);

        dispatch({
          type: 'ADD_ITEM_TO_CART',
          payload: { type: 'diamond', id: diamond.list_service_diamond_id }
        });
      }
    },
    [
      diamond.list_service_diamond_id,
      compatibility.recommended,
      currentSettingSku
    ]
  );

  let actions = [{ name: 'addToBag', title: `To Shopping Bag` }];

  if (
    currentDyorDiamondId !== diamond.list_service_diamond_id &&
    (compatibility.compatible || compatibility.recommended)
  ) {
    actions = [
      {
        name: 'addToDyor',
        title: `To ${currentSettingSku ? 'your' : 'a'} Setting`
      },
      ...actions
    ];
  }

  if (
    savedDiamonds.findIndex(el => el === diamond.list_service_diamond_id) === -1
  ) {
    actions.push({ name: 'addToCompare', title: 'To Comparison Page' });
  }

  return (
    <div className={classes.container}>
      <div className={classes.leftSection}>
        <CertificateInfo url={diamond.certificate_url} />
      </div>
      <div className={classes.rightSection}>
        <ComboButton
          actions={actions}
          onClick={handleItemClick}
          initialText='Add This Diamond'
        />
        <BackToSearchLink url={searchHref} />
      </div>
    </div>
  );
}
